import { Modal, ModalClose, ModalDialog } from '@mui/joy';

import { FC, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import {
  DemoConfig,
  EPromptOptimizationAlgorithm,
  OptimizationConfigForForm,
  PromptimConfigForForm,
} from '@/types/schema';

import { AlgorithmConfig } from './AlgorithmConfig';
import { BasicConfig } from './BasicConfig';
import { ConfigureOptimizeLayout, Step } from './ConfigureOptimizeLayout';
import { useOptimize } from './hooks/useOptimize';

type TProps = {
  isOpen: boolean;
  onClose: () => void;
  initialPromptIdentifier: string;
};

const defaultConfigByAlgorithm = {
  [EPromptOptimizationAlgorithm.PROMPTIM]: {
    message_index: 0,
    task_description: '',
    dataset_name: '',
    dataset_id: '',
    train_split: null,
    dev_split: null,
    test_split: null,
    evaluators: [],
    num_epochs: 3,
    auto_commit: true,
  } as PromptimConfigForForm,
  [EPromptOptimizationAlgorithm.DEMO]: {
    message_index: 0,
    task_description: '',
    metaprompt: {},
    examples: [{}],
  } as DemoConfig,
};

export const ConfigureOptimizeModal: FC<TProps> = ({
  isOpen,
  onClose,
  initialPromptIdentifier,
}) => {
  'use no memo';
  const [step, setStep] = useState(Step.BasicConfig);

  const defaultValues: OptimizationConfigForForm = {
    prompt_name: initialPromptIdentifier,
    algorithm: EPromptOptimizationAlgorithm.PROMPTIM,
    config: defaultConfigByAlgorithm[EPromptOptimizationAlgorithm.PROMPTIM],
  };

  const methods = useForm<OptimizationConfigForForm>({
    mode: 'onChange',
    defaultValues,
  });

  const onCloseModal = () => {
    onClose();
    setStep(Step.BasicConfig);
  };

  const { handleOptimize, isOptimizeLoading } = useOptimize();

  const onSubmit: SubmitHandler<OptimizationConfigForForm> = async (data) => {
    await handleOptimize(data);
    onCloseModal();
  };

  const algorithm = methods.watch('algorithm');

  return (
    <Modal open={isOpen} onClose={onCloseModal} sx={{ zIndex: 100 }}>
      <ModalDialog>
        <ModalClose />
        <ConfigureOptimizeLayout step={step} setStep={setStep}>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col gap-6"
            >
              {step == Step.BasicConfig && (
                <BasicConfig
                  nextStep={() => setStep(Step.AlgorithmConfig)}
                  onClose={onCloseModal}
                />
              )}
              {step == Step.AlgorithmConfig && (
                <AlgorithmConfig
                  algorithm={algorithm}
                  isOptimizeLoading={isOptimizeLoading}
                  onClose={onCloseModal}
                  prevStep={() => setStep(Step.BasicConfig)}
                />
              )}
            </form>
          </FormProvider>
        </ConfigureOptimizeLayout>
      </ModalDialog>
    </Modal>
  );
};
